import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  ArrowRight,
  ArrowLeft,
  Check,
  Building2,
  ShoppingBag,
  Cpu,
  Plane,
  Banknote,
  Briefcase,
  Layers,
  Dice5,
  TrendingUp,
  Heart,
} from "lucide-react";
import NeptuneMark from "./NeptuneMark";

const NEPTUNE_EMAIL = "hello@neptunepay.io";

const BUSINESS_TYPES = [
  { id: "E-commerce", label: "E-commerce", Icon: ShoppingBag },
  { id: "Marketplace", label: "Marketplace", Icon: Layers },
  { id: "SaaS", label: "SaaS", Icon: Cpu },
  { id: "Travel", label: "Travel", Icon: Plane },
  { id: "Fintech", label: "Fintech", Icon: Banknote },
  { id: "iGaming", label: "iGaming", Icon: Dice5 },
  { id: "Forex", label: "Forex", Icon: TrendingUp },
  { id: "Adult", label: "Adult", Icon: Heart },
  { id: "Enterprise", label: "Enterprise", Icon: Building2 },
  { id: "Other", label: "Other", Icon: Briefcase },
];

const VOLUMES = [
  "<$50k",
  "$50k–$250k",
  "$250k–$1M",
  "$1M–$5M",
  "$5M+",
];

const TITLES = {
  contact: {
    title: "Talk to Neptune",
    sub: "Tell us about your business — we'll route you to the right specialist within one business day.",
  },
  sales: {
    title: "Talk to Sales",
    sub: "Get a tailored quote and migration plan from our payments experts.",
  },
  started: {
    title: "Get Started with Neptune",
    sub: "Three quick steps and we'll have you set up to scale globally.",
  },
};

const initialForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  business_type: "",
  monthly_volume: "",
  message: "",
};

export default function LeadModal({ isOpen, onClose, intent = "contact" }) {
  const [step, setStep] = useState(1); // 1 -> 2 -> 3 -> success
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const meta = TITLES[intent] || TITLES.contact;

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep(1);
        setForm(initialForm);
        setSuccess(false);
        setError("");
        setSubmitting(false);
      }, 300);
    }
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const step1Valid = useMemo(
    () =>
      form.name.trim().length > 1 &&
      /\S+@\S+\.\S+/.test(form.email) &&
      form.company.trim().length > 1,
    [form]
  );

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const subject = `Neptune — New ${intent} lead from ${form.company || form.name}`;
      const body = [
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Company: ${form.company}`,
        form.phone ? `Phone: ${form.phone}` : null,
        `Business type: ${form.business_type}`,
        `Monthly volume: ${form.monthly_volume}`,
        `Source: ${intent}`,
        "",
        "Message:",
        form.message || "(no message)",
      ]
        .filter(Boolean)
        .join("\n");
      const mailto = `mailto:${NEPTUNE_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.location.href = mailto;
      setSuccess(true);
      setStep(4);
    } catch (e) {
      setError("Could not open your email client. Please email hello@neptunepay.io directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          data-testid="lead-modal"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
            onClick={onClose}
            data-testid="lead-modal-backdrop"
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg rounded-3xl border border-white/[0.08] bg-[#0c0c0e]/95 backdrop-blur-2xl shadow-[0_30px_120px_-20px_rgba(0,85,255,0.45)] overflow-hidden"
          >
            {/* Aura */}
            <div className="pointer-events-none absolute -top-32 -right-24 h-80 w-80 rounded-full bg-[#0055FF]/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-32 -left-24 h-80 w-80 rounded-full bg-[#00E5FF]/15 blur-3xl" />

            {/* Close */}
            <button
              onClick={onClose}
              data-testid="lead-modal-close"
              aria-label="Close"
              className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-zinc-300 hover:text-white hover:bg-white/[0.08] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Content */}
            <div className="relative p-7 sm:p-9">
              {/* Header */}
              <div className="flex items-center gap-2.5">
                <NeptuneMark size={20} />
                <span className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                  Neptune
                </span>
              </div>

              {step < 4 ? (
                <>
                  <h2
                    data-testid="lead-modal-title"
                    className="font-display mt-4 text-2xl sm:text-[28px] font-semibold tracking-tight chrome-text leading-tight"
                  >
                    {meta.title}
                  </h2>
                  <p className="mt-2 text-[14px] text-zinc-400 leading-relaxed">
                    {meta.sub}
                  </p>

                  {/* Stepper */}
                  <div className="mt-6 flex items-center gap-2">
                    {[1, 2, 3].map((n) => (
                      <div
                        key={n}
                        className={`h-1 rounded-full flex-1 transition-colors ${
                          step >= n
                            ? "bg-gradient-to-r from-[#00E5FF] to-[#0055FF]"
                            : "bg-white/[0.07]"
                        }`}
                        data-testid={`lead-step-indicator-${n}`}
                      />
                    ))}
                  </div>
                  <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                    Step {step} of 3
                  </div>

                  {/* Steps */}
                  <div className="mt-6 min-h-[260px]">
                    <AnimatePresence mode="wait">
                      {step === 1 && (
                        <motion.div
                          key="s1"
                          initial={{ opacity: 0, x: 12 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -12 }}
                          transition={{ duration: 0.25 }}
                          className="space-y-3.5"
                          data-testid="lead-step-1"
                        >
                          <Field
                            label="Full name"
                            placeholder="Alex Morgan"
                            value={form.name}
                            onChange={(v) => setField("name", v)}
                            testId="lead-input-name"
                          />
                          <Field
                            type="email"
                            label="Work email"
                            placeholder="alex@company.com"
                            value={form.email}
                            onChange={(v) => setField("email", v)}
                            testId="lead-input-email"
                          />
                          <Field
                            label="Company"
                            placeholder="Acme Inc."
                            value={form.company}
                            onChange={(v) => setField("company", v)}
                            testId="lead-input-company"
                          />
                          <Field
                            label="Phone (optional)"
                            placeholder="+1 555 000 1234"
                            value={form.phone}
                            onChange={(v) => setField("phone", v)}
                            testId="lead-input-phone"
                          />
                        </motion.div>
                      )}

                      {step === 2 && (
                        <motion.div
                          key="s2"
                          initial={{ opacity: 0, x: 12 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -12 }}
                          transition={{ duration: 0.25 }}
                          data-testid="lead-step-2"
                        >
                          <Label>Business type</Label>
                          <div className="grid grid-cols-2 gap-2.5 mt-3">
                            {BUSINESS_TYPES.map((b) => {
                              const active = form.business_type === b.id;
                              return (
                                <button
                                  key={b.id}
                                  type="button"
                                  onClick={() => setField("business_type", b.id)}
                                  data-testid={`lead-business-${b.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                  className={`group flex items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-all ${
                                    active
                                      ? "border-[#00E5FF]/40 bg-white/[0.04] shadow-[0_0_0_1px_rgba(0,229,255,0.15)_inset]"
                                      : "border-white/[0.07] bg-white/[0.015] hover:bg-white/[0.04] hover:border-white/[0.15]"
                                  }`}
                                >
                                  <div
                                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg border ${
                                      active
                                        ? "border-[#00E5FF]/30 text-white"
                                        : "border-white/[0.08] text-zinc-300"
                                    }`}
                                  >
                                    <b.Icon className="h-4 w-4" strokeWidth={1.5} />
                                  </div>
                                  <span className="text-[13.5px] text-white">
                                    {b.label}
                                  </span>
                                  {active && (
                                    <Check className="ml-auto h-4 w-4 text-[#00E5FF]" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}

                      {step === 3 && (
                        <motion.div
                          key="s3"
                          initial={{ opacity: 0, x: 12 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -12 }}
                          transition={{ duration: 0.25 }}
                          data-testid="lead-step-3"
                        >
                          <Label>Estimated monthly payment volume</Label>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {VOLUMES.map((v) => {
                              const active = form.monthly_volume === v;
                              return (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => setField("monthly_volume", v)}
                                  data-testid={`lead-volume-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                  className={`px-3.5 py-2 rounded-full text-[13px] border transition-all ${
                                    active
                                      ? "border-[#00E5FF]/40 bg-white/[0.06] text-white"
                                      : "border-white/[0.08] bg-white/[0.015] text-zinc-300 hover:bg-white/[0.04]"
                                  }`}
                                >
                                  {v}
                                </button>
                              );
                            })}
                          </div>

                          <div className="mt-5">
                            <Label>Anything we should know? (optional)</Label>
                            <textarea
                              value={form.message}
                              onChange={(e) => setField("message", e.target.value)}
                              placeholder="Current stack, pain points, target markets…"
                              rows={4}
                              data-testid="lead-input-message"
                              className="mt-2 w-full rounded-xl bg-white/[0.02] border border-white/[0.08] px-3.5 py-3 text-[14px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00E5FF]/40 focus:bg-white/[0.04] transition-colors resize-none"
                            />
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {error && (
                    <div
                      className="mt-3 text-[12.5px] text-rose-400"
                      data-testid="lead-error"
                    >
                      {error}
                    </div>
                  )}

                  {/* Footer actions */}
                  <div className="mt-6 flex items-center justify-between gap-3">
                    {step > 1 ? (
                      <button
                        onClick={() => setStep((s) => s - 1)}
                        data-testid="lead-back-btn"
                        className="inline-flex items-center gap-1.5 text-[13px] text-zinc-400 hover:text-white transition-colors px-2"
                      >
                        <ArrowLeft className="h-3.5 w-3.5" /> Back
                      </button>
                    ) : (
                      <span className="text-[12px] text-zinc-600">
                        We respect your privacy.
                      </span>
                    )}

                    {step < 3 ? (
                      <button
                        onClick={() => {
                          if (step === 1 && !step1Valid) {
                            setError("Please fill in name, work email and company.");
                            return;
                          }
                          if (step === 2 && !form.business_type) {
                            setError("Please select a business type.");
                            return;
                          }
                          setError("");
                          setStep((s) => s + 1);
                        }}
                        data-testid="lead-next-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-5 py-2.5 text-[13.5px] font-medium"
                      >
                        Continue
                        <ArrowRight className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (!form.monthly_volume) {
                            setError("Please pick an estimated volume.");
                            return;
                          }
                          submit();
                        }}
                        disabled={submitting}
                        data-testid="lead-submit-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors px-5 py-2.5 text-[13.5px] font-medium"
                      >
                        {submitting ? "Sending…" : "Submit"}
                        {!submitting && <ArrowRight className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <SuccessView
                  name={form.name}
                  email={form.email}
                  onClose={onClose}
                />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Label({ children }) {
  return (
    <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="mt-2 w-full rounded-xl bg-white/[0.02] border border-white/[0.08] px-3.5 py-2.5 text-[14px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00E5FF]/40 focus:bg-white/[0.04] transition-colors"
      />
    </div>
  );
}

function SuccessView({ name, onClose }) {
  return (
    <div className="text-center py-6" data-testid="lead-success">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto h-14 w-14 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#0055FF] flex items-center justify-center shadow-[0_0_30px_rgba(0,85,255,0.5)]"
      >
        <Check className="h-6 w-6 text-white" strokeWidth={2.5} />
      </motion.div>
      <h3 className="font-display mt-5 text-2xl sm:text-3xl font-semibold tracking-tight chrome-text">
        Thanks{name ? `, ${name.split(" ")[0]}` : ""}.
      </h3>
      <p className="mt-3 text-[14px] text-zinc-400 max-w-sm mx-auto leading-relaxed">
        A Neptune specialist will be in touch within one business day with a
        tailored next step.
      </p>
      <button
        onClick={onClose}
        data-testid="lead-success-close"
        className="mt-7 inline-flex items-center justify-center rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-5 py-2.5 text-[13.5px] font-medium"
      >
        Close
      </button>
    </div>
  );
}
